import { useCallback, useRef, useState } from "react";

import { tapToFocus } from "../modes/PreviewMode/PreviewMode.utils";

export interface TapIndicator {
    x: number;
    y: number;
    id: number;
}

interface UseTapToFocusReturn {
    indicator: TapIndicator | null;
    handleTap: (e: React.PointerEvent<HTMLElement>) => Promise<void>;
}

/**
 * Hook for implementing tap-to-focus (like in native camera apps).
 * Returns focus indicator coordinates and a tap handler.
 *
 * @param stream — current media stream from the camera
 * @param enabled — whether tap-to-focus is enabled (e.g., disabled during capture)
 */
export const useTapToFocus = (
    stream: MediaStream | null,
    enabled = true,
): UseTapToFocusReturn => {
    const [indicator, setIndicator] = useState<TapIndicator | null>(null);
    const timerRef = useRef<number | null>(null);

    const handleTap = useCallback(
        async (e: React.PointerEvent<HTMLElement>) => {
            if (!stream || !enabled) return;

            const track = stream.getVideoTracks()[0];
            if (!track) return;

            const rect = e.currentTarget.getBoundingClientRect();

            // Position for the indicator (in px relative to the container)
            const indicatorX = e.clientX - rect.left;
            const indicatorY = e.clientY - rect.top;

            // Normalized coordinates 0–1 for the camera API
            const normalizedX = indicatorX / rect.width;
            const normalizedY = indicatorY / rect.height;

            // Show the indicator and reset the previous timer
            if (timerRef.current !== null) clearTimeout(timerRef.current);
            setIndicator({ x: indicatorX, y: indicatorY, id: Date.now() });

            timerRef.current = window.setTimeout(() => {
                setIndicator(null);
                timerRef.current = null;
            }, 900);

            await tapToFocus(track, normalizedX, normalizedY);
        },
        [stream, enabled],
    );

    return { indicator, handleTap };
};
